import { create } from "zustand";
import type { CanvasObject, CanvasOp, TransformObjectOp } from "@refinement-canvas/domain";

// キャンバスのドキュメント状態（メタ情報とオブジェクト一覧）を管理する Zustand store。
// サーバ権威のスナップショットと、自分が送ったがまだ ack されていない inFlightOps を分離保持する。

export interface CanvasMetaState {
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly thumbnailKey?: string | undefined;
  readonly thumbnailUrl?: string | undefined;
}

interface CanvasDocState {
  canvasId: string | null;
  serverSeq: number;
  meta: CanvasMetaState;
  /** サーバ確定スナップショット */
  objects: Record<string, CanvasObject>;
  /** ローカルで先行適用した op（ack 待ち） */
  inFlightOps: CanvasOp[];

  loadSnapshot: (params: {
    canvasId: string;
    serverSeq: number;
    objects: CanvasObject[];
    meta?: CanvasMetaState | undefined;
  }) => void;
  updateLocalMeta: (meta: Partial<CanvasMetaState>) => void;
  applyOptimistic: (op: CanvasOp) => void;
  /** サーバから op.commit を受信したときに呼ぶ */
  onServerCommit: (op: CanvasOp, serverSeq: number) => void;
  /** サーバから op.reject を受信したときに呼ぶ（自分の op が拒否） */
  onServerReject: (clientOpId: string) => void;
  /** 接続切断などで完全リセットするとき */
  reset: () => void;
}

const DEFAULT_META: CanvasMetaState = {
  title: "無題のキャンバス",
  createdAt: "",
  updatedAt: "",
};

export const useCanvasDocStore = create<CanvasDocState>((set) => ({
  canvasId: null,
  serverSeq: 0,
  meta: DEFAULT_META,
  objects: {},
  inFlightOps: [],

  loadSnapshot: ({ canvasId, serverSeq, objects, meta }) =>
    set((state) => ({
      canvasId,
      serverSeq,
      meta: meta ?? (state.canvasId === canvasId ? state.meta : DEFAULT_META),
      objects: Object.fromEntries(objects.map((o) => [o.id, o])),
      inFlightOps: [],
    })),

  updateLocalMeta: (meta) =>
    set((state) => ({
      meta: {
        ...state.meta,
        ...meta,
      },
    })),

  applyOptimistic: (op) =>
    set((state) => {
      const next = applyOpToState({ meta: state.meta, objects: state.objects }, op);
      return {
        inFlightOps: [...state.inFlightOps, op],
        meta: next.meta,
        objects: next.objects,
      };
    }),

  onServerCommit: (op, serverSeq) =>
    set((state) => {
      // 自分が送った op の ack（clientOpId 一致）なら inFlightOps から取り除き、
      // サーバ確定値に置き換える。他者の op はそのまま objects/meta に反映する。
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
      const next = applyOpToState({ meta: state.meta, objects: state.objects }, op);
      return {
        serverSeq,
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  onServerReject: (clientOpId) =>
    set((state) => {
      const target = state.inFlightOps.find((p) => p.clientOpId === clientOpId);
      if (!target) return state;
      // 簡略化: 拒否された op を inFlight から外す。必要時は resync.required で補正する。
      return {
        inFlightOps: state.inFlightOps.filter((p) => p.clientOpId !== clientOpId),
      };
    }),

  reset: () => set({ canvasId: null, serverSeq: 0, meta: DEFAULT_META, objects: {}, inFlightOps: [] }),
}));

function applyOpToState(
  state: { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> },
  op: CanvasOp
): { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> } {
  return {
    meta: applyOpToMeta(state.meta, op),
    objects: applyOpToObjects(state.objects, op),
  };
}

function applyOpToMeta(meta: CanvasMetaState, op: CanvasOp): CanvasMetaState {
  if (op.type !== "canvas.meta.update") return meta;
  return {
    ...meta,
    ...(op.title !== undefined ? { title: op.title, updatedAt: op.clientCreatedAt } : {}),
    ...(op.thumbnailKey !== undefined
      ? { thumbnailKey: op.thumbnailKey, updatedAt: op.clientCreatedAt }
      : {}),
  };
}

/**
 * 単一 op を objects 連想配列に適用する。
 * サーバ権威モデルでは、同じ関数を server / client の両方で適用することで決定論的に状態を再現する。
 */
function applyOpToObjects(
  objects: Record<string, CanvasObject>,
  op: CanvasOp
): Record<string, CanvasObject> {
  switch (op.type) {
    case "object.create":
      return { ...objects, [op.object.id]: op.object };
    case "object.delete": {
      const next = { ...objects };
      delete next[op.objectId];
      return next;
    }
    case "object.transform": {
      const current = objects[op.objectId];
      if (!current) return objects;
      return {
        ...objects,
        [op.objectId]: applyObjectTransform(current, op.transform),
      };
    }
    case "object.text.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      return {
        ...objects,
        [op.objectId]: { ...current, text: op.text } as CanvasObject,
      };
    }
    case "pen.stroke.commit":
      return { ...objects, [op.object.id]: op.object };
    case "object.attachment.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      // image / video / link-card に共通する src/imageUrl 系のフィールドを更新する。
      if (current.type === "image" || current.type === "video") {
        return {
          ...objects,
          [op.objectId]: { ...current, src: op.src } as CanvasObject,
        };
      }
      if (current.type === "link-card") {
        return {
          ...objects,
          [op.objectId]: { ...current, imageUrl: op.src } as CanvasObject,
        };
      }
      return objects;
    }
    case "canvas.meta.update":
      return objects;
    default:
      return objects;
  }
}

function applyObjectTransform(
  current: CanvasObject,
  transform: TransformObjectOp["transform"]
): CanvasObject {
  if (current.type !== "pen-stroke") {
    return { ...current, ...transform } as CanvasObject;
  }

  const nextX = transform.x ?? current.x;
  const nextY = transform.y ?? current.y;
  const dx = nextX - current.x;
  const dy = nextY - current.y;

  // ペンストロークの points は stage 座標で保持しているため、
  // オブジェクト移動時は外接矩形と同じ差分だけポイント群も移動する。
  return {
    ...current,
    ...transform,
    points:
      dx === 0 && dy === 0
        ? current.points
        : current.points.map((point) => ({ ...point, x: point.x + dx, y: point.y + dy })),
  };
}
